import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { RefreshCw, Save, Instagram, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function AdminInstagram() {
  const { refresh } = useSettings();
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => { api.get("/settings").then(({ data }) => setData(data)); }, []);
  if (!data) return <div className="p-8">Loading...</div>;

  const set = (k, v) => setData({ ...data, instagram: { ...(data.instagram || {}), [k]: v } });
  const ig = data.instagram || {};

  const save = async () => { try { await api.put("/settings", { data }); toast.success("Saved"); refresh(); } catch { toast.error("Save failed"); } };
  const sync = async () => {
    setSyncing(true);
    try {
      const { data: res } = await api.post("/admin/instagram/sync");
      toast.success(`Added ${res.added} new items (${res.skipped} skipped)`);
      // Refresh settings to show last_sync
      const { data: s } = await api.get("/settings");
      setData(s);
    } catch (e) { toast.error(e.response?.data?.detail || "Sync failed. Check credentials."); }
    setSyncing(false);
  };

  const cls = "w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm";
  const Field = ({ label, k, type = "text" }) => (
    <div>
      <label className="text-xs font-semibold text-neutral-600">{label}</label>
      <input type={type} value={ig[k] || ""} onChange={(e) => set(k, e.target.value)} className={cls} data-testid={`ig-${k}`} />
    </div>
  );

  const connected = !!(ig.access_token && ig.business_account_id);

  return (
    <div className="p-8 max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-orange-400 flex items-center justify-center"><Instagram className="w-5 h-5 text-white" /></div>
            <h1 className="font-display font-black text-3xl" data-testid="admin-instagram-title">Instagram Sync</h1>
          </div>
          <p className="text-neutral-500 mt-2">Connect the Meta Graph API to auto-import posts and reels into the Gallery.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={sync} disabled={!connected || syncing} className="btn-outline-eq text-sm disabled:opacity-50" data-testid="ig-sync">
            <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing..." : "Sync now"}
          </button>
          <button onClick={save} className="btn-primary-eq text-sm" data-testid="ig-save"><Save className="w-4 h-4" /> Save</button>
        </div>
      </div>

      <div className={`p-4 rounded-2xl border mb-6 ${connected ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid="ig-status">
        <div className="text-sm">
          <b>Status:</b> {connected ? "Credentials configured — ready to sync." : "Missing credentials. Add access token + business account ID below."}
        </div>
        {ig.last_sync && <div className="text-xs mt-1 text-neutral-600">Last sync: {ig.last_sync} — {ig.last_sync_status}</div>}
      </div>

      <div className="grid gap-6">
        <div className="bg-white rounded-2xl p-6 border border-neutral-200">
          <h2 className="font-display font-bold text-lg mb-4">Profile</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Instagram profile URL" k="profile_url" />
            <Field label="Instagram username" k="username" />
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-neutral-200">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="font-display font-bold text-lg">Meta Graph API</h2>
              <p className="text-xs text-neutral-500 mt-1">You need an Instagram Business account linked to a Facebook Page.</p>
            </div>
            <a href="https://developers.facebook.com/docs/instagram-api/getting-started" target="_blank" rel="noreferrer" className="text-xs text-sky-600 hover:underline inline-flex items-center gap-1">Setup guide <ExternalLink className="w-3 h-3" /></a>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Meta App ID" k="app_id" />
            <Field label="Meta App Secret" k="app_secret" type="password" />
            <Field label="Access token (long-lived)" k="access_token" type="password" />
            <Field label="Instagram Business Account ID" k="business_account_id" />
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-neutral-200">
          <h2 className="font-display font-bold text-lg mb-4">Sync options</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-neutral-600">Feed limit (max items per sync)</label>
              <input type="number" value={ig.feed_limit || 12} onChange={(e) => set("feed_limit", Number(e.target.value))} className={cls} data-testid="ig-feed-limit" />
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-600">Cache duration (hours)</label>
              <input type="number" value={ig.cache_hours || 6} onChange={(e) => set("cache_hours", Number(e.target.value))} className={cls} />
            </div>
          </div>
          <div className="mt-4 space-y-2">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!ig.show_posts} onChange={(e) => set("show_posts", e.target.checked)} /> Show posts</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!ig.show_reels} onChange={(e) => set("show_reels", e.target.checked)} /> Show reels</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!ig.auto_sync} onChange={(e) => set("auto_sync", e.target.checked)} /> Auto-sync every {ig.cache_hours || 6}h (requires cron)</label>
          </div>
        </div>
      </div>
    </div>
  );
}
